import React, { useState } from 'react';
import * as randomWords from 'random-words';
import './ImportForm.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Loader from './Loader/Loader';
import Input from './Input/Input';
import Button from './Button/Button';

const timestamp = new Date();
let interval = setInterval(() => {}, 1000000000);

const DEFAULT_FLOTIQ_KEY = process.env.REACT_APP_DEFAULT_FLOTIQ_KEY;
const DEFAULT_WP_URL = process.env.REACT_APP_DEFAULT_WP_URL;
const WP_IMPORT_AUTH = process.env.REACT_APP_WP_IMPORT_AUTH;
const WP_IMPORT_STATUS = process.env.REACT_APP_WP_IMPORT_STATUS;
const USER_LOCALSTORAGE_KEY = 'flotiq-wp-import-user-code';

const CHECK_STATUS_SUCCESS = 'success';
const CHECK_STATUS_ERROR = 'error';
const CHECK_STATUS_IN_PROGRESS = 'in progress';
const MODE_CHECK = 'check';
const MODE_SUBMIT = 'submit';

const requestCodeGenerator = () => {
    const date = timestamp.toISOString().split('T')[0];
    const UcFirst = (word) => word.charAt(0).toUpperCase() + word.slice(1);
    const word1 = UcFirst(randomWords());
    const word2 = UcFirst(randomWords());

    return word1 + word2 + date;
};

const ImportForm = () => {
    const [flotiqApiKey, setFlotiqApiKey] = useState(DEFAULT_FLOTIQ_KEY);
    const [wordpressUrl, setWordpressUrl] = useState(DEFAULT_WP_URL);
    const [userRequestCode, setUserRequestCode] = useState(localStorage[USER_LOCALSTORAGE_KEY]);
    const [progress, setProgress] = useState();
    const [errors, setErrors] = useState();
    const [mode, setMode] = useState(MODE_SUBMIT);

    const checkStatus = async (userRequestCodeVal) => {
        let statusRequest;
        setProgress(true);
        setErrors('');

        try {
            statusRequest = await fetch(
                WP_IMPORT_STATUS + userRequestCodeVal, {
                    method: 'GET',
                },
            );
        } catch (e) {
            setProgress(false);
            setErrors('Status update check failed. Try again.');
            return;
        }

        // przyszło response {} oznaczało że nie znalazło kodu
        const responseBody = await statusRequest.json();
        if (responseBody.Item?.status?.S) {
            switch (responseBody.Item.status.S) {
            case CHECK_STATUS_SUCCESS:
                setProgress(false);
                clearInterval(interval);
                break;
            case CHECK_STATUS_ERROR:
                setProgress(false);
                clearInterval(interval);
                setErrors('Importer error. Please try again.');
                break;
            case CHECK_STATUS_IN_PROGRESS:
            default:
                // @todo optional progress status info
                break;
            }
        } else {
            setProgress(false);
            setErrors('There is no import with that request code');
        }
    };

    const submit = async () => {
        const userCode = requestCodeGenerator();
        clearInterval(interval);
        setProgress(true);
        setErrors('');
        setUserRequestCode(userCode);
        localStorage.setItem(USER_LOCALSTORAGE_KEY, userCode);

        const url = `${WP_IMPORT_AUTH
        }?userRequestCode=${userCode}`
            + `&flotiqApiKey=${flotiqApiKey}`
            + `&wordpressUrl=${wordpressUrl}`;

        const res = await fetch(url, {
            method: 'GET',
        });

        if (res.status === 200) {
            interval = setInterval(async () => {
                await checkStatus(userCode);
            }, 3000);
        } else {
            const jsonResponse = await res.json();
            setProgress(false);
            setErrors(jsonResponse.error || 'Importer error. Please try again.');
        }
    };

    const changeFormMode = (event, formMode) => {
        event.preventDefault();
        setMode(formMode);
    };

    const apiHelpText = (
        <>
            Access your API key in the
            {' '}
            <a
                href="https://editor.flotiq.com/register.html"
                target="_blank"
                rel="noreferrer"
                title="Flotiq dashboard registration"
                className="underline"
            >
                Flotiq dashboard
            </a>
            .
            See more information about API keys in
            {' '}
            <a
                href="https://flotiq.com/docs/API/"
                target="_blank"
                rel="noreferrer"
                title="Flotiq documentation"
                className="underline"
            >
                Flotiq docs
            </a>
            .
        </>
    );

    const submitForm = (
        <form onSubmit={submit}>
            <div className="px-8 pt-8">
                <Input
                    name="apikey"
                    label="Flotiq Read and Write API Key"
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    type="text"
                    value={flotiqApiKey}
                    onChange={(event) => setFlotiqApiKey(event.target.value)}
                    helpText={apiHelpText}
                />
                <Input
                    name="wpurl"
                    label="WordPress full URL"
                    placeholder="https://your-wp-blog.com"
                    type="url"
                    value={wordpressUrl}
                    onChange={(event) => setWordpressUrl(event.target.value)}
                />
            </div>
            <div className="px-8 pt-2">
                <Button
                    label="Start import"
                    submit={submit}
                    disabled={progress}
                />
                <p className="mt-1 text-sm text-gray-500">
                    If you want to check import status,
                    {' '}
                    <a
                        href="/"
                        className="underline"
                        onClick={(e) => changeFormMode(e, MODE_CHECK)}
                    >
                        click here
                    </a>
                </p>
            </div>
        </form>
    );

    const checkForm = (
        <form onSubmit={() => checkStatus(userRequestCode)}>
            <div className="px-8 pt-8">
                <Input
                    name="usercode"
                    label="Import code"
                    placeholder="your import code"
                    type="text"
                    value={userRequestCode}
                    onChange={(event) => setUserRequestCode(event.target.value)}
                />
                <Button
                    label="Check import status"
                    submit={() => checkStatus(userRequestCode)}
                    disabled={progress}
                />
                <p className="mt-1 text-sm text-gray-500">
                    If you want to import new data,
                    {' '}
                    <a
                        href="/"
                        className="underline"
                        onClick={(e) => changeFormMode(e, MODE_SUBMIT)}
                    >
                        click here
                    </a>
                </p>
            </div>
        </form>
    );
    const mailto = 'mailto:hello@flotiq.com?'
        + 'subject=Error%20on%20wordpress%20importer'
        + `&body=userRequestCode:%20${userRequestCode}`;

    const statusInfo = (
        <div className="px-8 pt-2 pb-8">
            {
                progress && (
                    <div className="pt-3 text-gray-700 text-sm">
                        <Loader />
                        Importing in progress...
                    </div>
                )
            }
            {
                errors && (
                    <div className="pt-3 text-red-500 text-sm">
                        <FontAwesomeIcon className=" mr-3" icon={faExclamationTriangle} />
                        {errors}
                        <p>
                            Please, send us an
                            {' '}
                            <a
                                href={mailto}
                                className="underline"
                            >
                                email with details of your error
                            </a>
                        </p>
                    </div>
                )
            }
            {
                progress === false && !errors && (
                    <div className="pt-3 text-green-800 text-sm">
                        <FontAwesomeIcon className=" mr-3" icon={faCheckCircle} />
                        Success! You can view the data in your
                        {' '}
                        <a href="https://editor.flotiq.com" className="font-bold">
                            Flotiq
                        </a>
                        {' '}
                        account.
                    </div>
                )
            }
        </div>
    );

    return (
        <div className="sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white shadow rounded-lg">
                <div className="px-8 py-6 border-b border-gray-200">
                    <h3 className="text-lg leading-6 font-medium text-gray-800">
                        Import data from WordPress
                    </h3>
                </div>
                { mode === MODE_SUBMIT && submitForm }
                { mode === MODE_CHECK && checkForm }
                { statusInfo }
            </div>
        </div>
    );
};

export default ImportForm;
